import {
  addMonths,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
  format,
  isAfter,
  isValid,
  parseISO,
} from 'date-fns';

/** A timestamp as the API or a caller hands it over. */
export type DateInput = string | number | Date;

/** A missing value is an invalid date, which formats as `Invalid date` as moment did, rather than a throw. */
const toDate = (value: number | Date | null | undefined) =>
  value == null ? new Date(NaN) : new Date(typeof value === 'number' ? value : value.getTime());

type ParseOptions = {
  /**
   * Reject strings that are not ISO 8601, as luxon's `fromISO` did, instead of
   * falling back to the `Date` parser (which reads `2026-02-30` as 2 March and
   * accepts `10/01/2026`).
   */
  isoOnly?: boolean;
};

/**
 * Parses an ISO string in the browser's time zone, as `moment(value)` did:
 * strings without an offset are local wall time. Strings that are not ISO 8601
 * fall back to the `Date` parser, as moment did, unless `isoOnly` is set. `null`,
 * `undefined` and unparseable values give an invalid date.
 */
export const parseLocalTime = (value: DateInput | null | undefined, { isoOnly = false }: ParseOptions = {}): Date => {
  if (typeof value !== 'string') {
    return toDate(value);
  }
  const parsed = parseISO(value);
  return isValid(parsed) || isoOnly ? parsed : new Date(value);
};

const TIME_OFFSET = /(?:Z|[+-]\d{2}(?::?\d{2})?)$/i;

/**
 * Parses an API timestamp, reading strings without an offset as UTC, as
 * `moment.utc(value)` did. Several API fields send `2026-10-01T12:00:00` with no
 * `Z`; `parseISO` alone would read those as local time. `null`, `undefined` and
 * unparseable values give an invalid date.
 */
export const parseApiTime = (value: DateInput | null | undefined, { isoOnly = false }: ParseOptions = {}): Date => {
  if (typeof value !== 'string') {
    return toDate(value);
  }
  const timeStart = value.search(/[T ]/);
  const withOffset =
    timeStart === -1 ? `${value}T00:00:00Z` : TIME_OFFSET.test(value.slice(timeStart + 1)) ? value : `${value}Z`;
  const parsed = parseISO(withOffset);
  return isValid(parsed) || isoOnly ? parsed : new Date(`${value} UTC`);
};

/** Whether `value` is an ISO 8601 date string, as `moment(value, moment.ISO_8601).isValid()` was. */
export const isIsoDateString = (value: unknown): value is string =>
  // parseISO also takes two-digit centuries (`12`); moment needed a four-digit year.
  typeof value === 'string' && /^(?:[+-]\d{6}|\d{4})/.test(value) && isValid(parseISO(value));

/** date-fns `format`, but an invalid date reads `Invalid date` (as moment did) instead of throwing. */
export const formatDateTime = (date: Date, pattern: string): string =>
  isValid(date) ? format(date, pattern) : 'Invalid date';

/** `2026-10-01`, the calendar date in UTC. An invalid date reads `Invalid date` instead of throwing. */
export const formatUtcDate = (date: Date): string => (isValid(date) ? date.toISOString().slice(0, 10) : 'Invalid date');

const RELATIVE_UNITS = {
  s: 'a few seconds',
  m: 'a minute',
  mm: '%d minutes',
  h: 'an hour',
  hh: '%d hours',
  d: 'a day',
  dd: '%d days',
  M: 'a month',
  MM: '%d months',
  y: 'a year',
  yy: '%d years',
} as const;

/**
 * Whole calendar months from `earlier` to `later`, counted the way moment and
 * luxon do: add months (clamping to the month's last day) while that stays at or
 * before `later`. date-fns' `differenceInMonths` treats month ends differently.
 */
const wholeMonthsBetween = (earlier: Date, later: Date) => {
  const months = (later.getFullYear() - earlier.getFullYear()) * 12 + later.getMonth() - earlier.getMonth();
  return isAfter(addMonths(earlier, months), later) ? months - 1 : months;
};

const signedWholeMonths = (date: Date, now: Date) =>
  date < now ? -wholeMonthsBetween(date, now) : wholeMonthsBetween(now, date);

const DAYS_PER_MONTH = 146097 / 4800;

/**
 * Short age label for a table cell, in whole units: `42m`, `30h`, `12d`, `7mo`, `1y 6mo`. Past two days it
 * moves to the largest unit that keeps the number small, so a year-old row reads `1y 6mo`, not `553.1d`.
 */
export const formatCompactAge = (minutes: number): string => {
  const safe = Math.max(0, minutes);
  if (safe < 60) {
    return `${Math.floor(safe)}m`;
  }
  const hours = safe / 60;
  if (hours < 48) {
    return `${Math.round(hours)}h`;
  }
  const days = Math.floor(hours / 24);
  if (days < 30) {
    return `${days}d`;
  }
  const months = Math.floor(days / DAYS_PER_MONTH);
  if (months < 12) {
    return `${Math.max(1, months)}mo`;
  }
  const years = Math.floor(months / 12);
  const rest = months % 12;
  return rest ? `${years}y ${rest}mo` : `${years}y`;
};

/**
 * Relative time in moment's `fromNow` wording ("a few seconds ago", "an hour
 * ago", "in 3 days"), with moment's thresholds and rounding, so existing labels
 * read the same.
 */
export const fromNow = (date: Date, now: Date = new Date()): string => {
  if (!isValid(date)) {
    return 'Invalid date';
  }
  const isFuture = date.getTime() > now.getTime();
  const [earlier, later] = isFuture ? [now, date] : [date, now];

  // moment splits the gap into whole calendar months plus the remaining milliseconds.
  const wholeMonths = wholeMonthsBetween(earlier, later);
  const remainderMs = later.getTime() - addMonths(earlier, wholeMonths).getTime();
  const totalDays = Math.round(wholeMonths * DAYS_PER_MONTH) + remainderMs / 864e5;

  const seconds = Math.round(totalDays * 86400);
  const minutes = Math.round(totalDays * 1440);
  const hours = Math.round(totalDays * 24);
  const days = Math.round(totalDays);
  const months = Math.round(wholeMonths + remainderMs / 864e5 / DAYS_PER_MONTH);
  const years = Math.round((wholeMonths + remainderMs / 864e5 / DAYS_PER_MONTH) / 12);

  const [unit, count]: [keyof typeof RELATIVE_UNITS, number] =
    seconds < 45
      ? ['s', seconds]
      : minutes <= 1
        ? ['m', 1]
        : minutes < 45
          ? ['mm', minutes]
          : hours <= 1
            ? ['h', 1]
            : hours < 22
              ? ['hh', hours]
              : days <= 1
                ? ['d', 1]
                : days < 26
                  ? ['dd', days]
                  : months <= 1
                    ? ['M', 1]
                    : months < 11
                      ? ['MM', months]
                      : years <= 1
                        ? ['y', 1]
                        : ['yy', years];

  const phrase = RELATIVE_UNITS[unit].replace('%d', String(count));
  return isFuture ? `in ${phrase}` : `${phrase} ago`;
};

const RELATIVE_DIFFERS = [
  ['year', (date: Date, now: Date) => Math.trunc(signedWholeMonths(date, now) / 12)],
  ['month', signedWholeMonths],
  ['day', differenceInDays],
  ['hour', differenceInHours],
  ['minute', differenceInMinutes],
  ['second', differenceInSeconds],
] as const;

/**
 * Relative time in luxon's `toRelative` wording ("2 days ago", "in 3 hours"):
 * the largest whole unit, counted toward zero, in the browser's locale.
 */
// Built once: constructing an Intl formatter is slow, and the locale (the browser's) does not change.
const relativeTimeFormatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'always' });

export const formatRelativeTime = (date: Date, now: Date = new Date()): string | null => {
  if (!isValid(date)) {
    return null;
  }
  for (const [unit, differ] of RELATIVE_DIFFERS) {
    const count = differ(date, now);
    if (Math.abs(count) >= 1) {
      return relativeTimeFormatter.format(count, unit);
    }
  }
  return relativeTimeFormatter.format(date < now ? -0 : 0, 'second');
};
